import {
  copyAttachmentToPosition,
  useGetAttachmentById,
} from "@/api/attachment";
import {
  attachmentKindToCmsMediaKind,
  cmsMediaTranslationKey,
} from "@/lib/cms/cms-media-labels";
import {
  CmsLanguageImageTarget,
  getSiblingCmsLanguageImageTargets,
} from "@/lib/cms/cms-language-image-positions";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { toast } from "@/hooks/use-toast";
import { Attachment } from "@/lib/types";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { SupportedLocale } from "@/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddCroppedImageForm, {
  AddCroppedImageFormProps,
} from "../images/AddCroppedImageForm";
import { MediaPreview } from "../media/MediaPreview";
import StyledDrawer from "../StyledDrawer";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "../ui/dialog";
import { languageToFlag } from "../icons/flags/flag-def";

type PendingLanguageSync = {
  attachment: Attachment;
  targets: CmsLanguageImageTarget[];
};

export const CroppedImageInput = ({
  imagePosition,
  refetch,
  attachment,
  label,
  buttonLabel,
  title,
  imageURL,
  docId,
  aspect = 0.78,
  buildImageName: pageSlug,
  onUpdate,
  onSuccess,
  forceCreateNewAttachment,
  hidePreview = false,
  drawerClassName,
  className,
  buttonSize = "default",
  inlineActions = false,
}: AddCroppedImageFormProps & {
  title?: string;
  label?: string;
  buttonLabel?: string;
  imageURL?: string;
  docId?: string;
  aspect?: number;
  /** Hide inline preview when the parent renders its own (e.g. delete overlay). */
  hidePreview?: boolean;
  drawerClassName?: string;
  className?: string;
  buttonSize?: "default" | "sm" | "lg" | "icon";
  /** Render only the drawer trigger (no label row). Use inside a parent flex toolbar. */
  inlineActions?: boolean;
}) => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSync, setPendingSync] = useState<PendingLanguageSync | null>(
    null,
  );
  const [selectedLocales, setSelectedLocales] = useState<SupportedLocale[]>(
    [],
  );
  const [awaitingVideoReady, setAwaitingVideoReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncConfirmedRef = useRef(false);
  const pendingSyncRef = useRef<PendingLanguageSync | null>(null);
  const selectedPositionsRef = useRef<string[]>([]);
  pendingSyncRef.current = pendingSync;

  const siblingTargets = useMemo(
    () => getSiblingCmsLanguageImageTargets(imagePosition, websiteLanguages),
    [imagePosition, websiteLanguages],
  );

  const selectedPositions = useMemo(() => {
    if (!pendingSync) return [];
    return pendingSync.targets
      .filter((target) => selectedLocales.includes(target.locale))
      .map((target) => target.position);
  }, [pendingSync, selectedLocales]);
  selectedPositionsRef.current = selectedPositions;

  const pollId =
    awaitingVideoReady && pendingSync?.attachment.id
      ? pendingSync.attachment.id
      : "";

  const { data: polledAttachment } = useGetAttachmentById(pollId, {
    pollWhileProcessing: true,
  });

  // The parent receives the "processing" placeholder via onSuccess, but the
  // drawer that was polling it is closed by then: poll it here (no request
  // unless it is a processing video) so the preview and the drawer see "ready".
  const liveId =
    attachment?.kind === "video" && attachment.videoStatus === "processing"
      ? (attachment.id ?? "")
      : "";
  const { data: liveData } = useGetAttachmentById(liveId, {
    pollWhileProcessing: true,
  });
  const liveAttachment: Attachment | undefined =
    liveId && liveData?.id === liveId ? liveData : attachment;

  const applyLanguageSync = async (source: Attachment, positions: string[]) => {
    if (positions.length === 0) {
      finishWithoutSync(source);
      return;
    }
    const sourceAttachmentId = source.id;
    if (!sourceAttachmentId) {
      finishWithoutSync(source);
      return;
    }
    setSyncing(true);
    try {
      for (const targetPosition of positions) {
        await copyAttachmentToPosition({
          sourceAttachmentId,
          targetPosition,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["attachment"] });
      refetch();
      toast({
        variant: "success",
        description: t("cms.add.media.syncLanguagesSuccess"),
        duration: 2000,
      });
      onSuccess?.(source);
    } catch (e) {
      console.error(e);
      toast({
        title: t("error"),
        description: t("cms.add.media.syncLanguagesError"),
        variant: "error",
        duration: 3000,
      });
      onSuccess?.(source);
    } finally {
      setSyncing(false);
      setPendingSync(null);
      setSelectedLocales([]);
      setAwaitingVideoReady(false);
      syncConfirmedRef.current = false;
    }
  };

  useEffect(() => {
    if (!awaitingVideoReady || !pendingSync || !syncConfirmedRef.current) {
      return;
    }
    if (polledAttachment?.videoStatus !== "ready") return;
    void applyLanguageSync(
      polledAttachment as Attachment,
      selectedPositionsRef.current,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when video becomes ready
  }, [awaitingVideoReady, polledAttachment?.videoStatus, polledAttachment?.id]);

  const finishWithoutSync = (created: Attachment) => {
    setPendingSync(null);
    setSelectedLocales([]);
    setAwaitingVideoReady(false);
    syncConfirmedRef.current = false;
    onSuccess?.(created);
  };

  const handleUploadSuccess = (
    created: Attachment,
    meta?: { replacedMedia?: boolean },
  ) => {
    if (meta?.replacedMedia && siblingTargets.length > 0) {
      setIsOpen(false);
      setPendingSync({
        attachment: created,
        targets: siblingTargets,
      });
      setSelectedLocales(siblingTargets.map((target) => target.locale));
      return;
    }
    setIsOpen(false);
    // Avoid notifying parent again while the language-sync dialog is open
    // (e.g. video "ready" poll after the placeholder already prompted sync).
    if (pendingSyncRef.current || syncConfirmedRef.current) return;
    onSuccess?.(created);
  };

  const toggleLocale = (locale: SupportedLocale) => {
    setSelectedLocales((prev) =>
      prev.includes(locale)
        ? prev.filter((l) => l !== locale)
        : [...prev, locale],
    );
  };

  const handleSyncConfirm = () => {
    if (!pendingSync || syncing) return;
    if (selectedPositions.length === 0) {
      finishWithoutSync(pendingSync.attachment);
      return;
    }
    syncConfirmedRef.current = true;
    const { attachment: source } = pendingSync;
    const stillProcessing =
      source.kind === "video" && source.videoStatus === "processing";
    if (stillProcessing) {
      setAwaitingVideoReady(true);
      toast({
        description: t("cms.add.media.syncLanguagesWaitingVideo"),
        duration: 2500,
      });
      return;
    }
    void applyLanguageSync(source, selectedPositions);
  };

  const handleSyncDismiss = () => {
    if (syncing || syncConfirmedRef.current) return;
    const created = pendingSync?.attachment;
    if (created) finishWithoutSync(created);
    else {
      setPendingSync(null);
      setSelectedLocales([]);
      setAwaitingVideoReady(false);
    }
  };

  const drawerLabels = useMemo(() => {
    const kind = attachmentKindToCmsMediaKind(attachment?.kind);
    return {
      buttonLabel: buttonLabel ?? t(cmsMediaTranslationKey("button", kind)),
      title: title ?? t(cmsMediaTranslationKey("title", kind)),
    };
  }, [attachment?.kind, buttonLabel, title, t]);

  const triggerButton = (
    <Button
      type="button"
      className={cn(
        "font-semibold flex gap-2",
        inlineActions && "w-full",
        !inlineActions && className,
      )}
      variant="outline"
      size={buttonSize}
    >
      {drawerLabels.buttonLabel}
    </Button>
  );

  const drawer = (
    <StyledDrawer
      className={drawerClassName ?? "w-full max-w-full sm:min-w-[600px]"}
      sheetProps={{
        open: isOpen,
        onOpenChange: setIsOpen,
      }}
      title={drawerLabels.title}
      trigger={triggerButton}
    >
      <AddCroppedImageForm
        docId={docId}
        attachment={liveAttachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={aspect}
        forceCreateNewAttachment={forceCreateNewAttachment}
        onUpdate={onUpdate}
        onSuccess={handleUploadSuccess}
        buildImageName={pageSlug}
      />
    </StyledDrawer>
  );

  const syncDialog = (
    <Dialog
      open={!!pendingSync && !awaitingVideoReady && !syncing}
      onOpenChange={(open) => {
        if (!open) handleSyncDismiss();
      }}
    >
      <DialogContent className="max-w-md p-6 gap-4">
        <p className="text-center text-sm font-medium text-slate-700">
          {t("cms.add.media.syncLanguagesConfirm")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {pendingSync?.targets.map(({ locale }) => {
            const LanguageFlag = languageToFlag[locale];
            const selected = selectedLocales.includes(locale);
            return (
              <Badge
                key={locale}
                variant={selected ? "containedActive" : "contained"}
                className="text-[14px] font-[500] font-[Raleway] cursor-pointer flex flex-row items-center gap-2 py-1 pr-[10px] pl-[12px]"
                onClick={() => toggleLocale(locale)}
              >
                <LanguageFlag width={16} height={16} />
                {t(`language_${locale}`)}
              </Badge>
            );
          })}
        </div>
        <DialogFooter className="flex justify-between gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleSyncDismiss}
          >
            {t("formCancel")}
          </Button>
          <Button
            type="button"
            className="w-full"
            disabled={selectedLocales.length === 0}
            onClick={handleSyncConfirm}
          >
            {t("cms.add.media.syncLanguagesYes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (inlineActions) {
    return (
      <div className={cn("flex-1 min-w-0 [&_button]:w-full", className)}>
        {drawer}
        {syncDialog}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      <div className="flex justify-between items-center gap-2 pt-2 w-full min-w-0">
        {label && (
          <p className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal min-w-0 shrink">
            {label}
          </p>
        )}
        <div className="shrink-0">{drawer}</div>
      </div>
      {!hidePreview && (
        <MediaPreview
          attachment={liveAttachment}
          previewImageUrl={imageURL}
          className="max-w-full"
        />
      )}
      {syncDialog}
    </div>
  );
};
